import { Component } from 'preact';
import { CLASS_SIGNS } from '../../utils/arena/classSigns.data';

// A class's star sign, drawn into the sky: the simplified figure redrawn
// from its engraving sits behind, tinted in the class colour
// through a CSS mask, and its stars, joined by constellation lines,
// carry the light. `lit` wakes the whole sign when the eye rests on it.
//
// Props: classKey, accent, lit, className, style

// A four-point star: spikes of length `len` tapering from a waist of `w`.
function sparkle(len, w) {
  return `M ${len} 0 L ${w} ${w} L 0 ${len} L ${-w} ${w} L ${-len} 0 L ${-w} ${-w} L 0 ${-len} L ${w} ${-w} Z`;
}

export default class ClassSignFigure extends Component {
  render() {
    const { classKey, accent, lit, className = '', style } = this.props;
    const sign = CLASS_SIGNS[classKey];
    if (!sign) return null;
    return (
      <div
        className={`vh-sign ${lit ? 'vh-sign-lit' : ''} ${className}`}
        style={{ aspectRatio: `${sign.width} / ${sign.height}`, '--vh-sign-accent': accent, ...style }}
        aria-hidden="true"
      >
        <div
          className="vh-sign-figure"
          style={{ WebkitMaskImage: `url("${sign.image}")`, maskImage: `url("${sign.image}")` }}
        />
        <svg className="vh-sign-stars" viewBox={`0 0 ${sign.width} ${sign.height}`} preserveAspectRatio="xMidYMid meet">
          <defs>
            {/* Glare that falls away with distance, the way a bright point
                spreads on a lens, instead of a blurred disc. */}
            <radialGradient id={`vh-sign-glare-${classKey}`}>
              <stop offset="0" stop-color="#fffaf0" stop-opacity="0.9" />
              <stop offset="0.14" style="stop-color: var(--vh-sign-accent, #e8c87a)" stop-opacity="0.55" />
              <stop offset="0.4" style="stop-color: var(--vh-sign-accent, #e8c87a)" stop-opacity="0.12" />
              <stop offset="1" style="stop-color: var(--vh-sign-accent, #e8c87a)" stop-opacity="0" />
            </radialGradient>
          </defs>
          {(sign.links || []).map(([a, b], i) => {
            const [ax, ay] = sign.stars[a];
            const [bx, by] = sign.stars[b];
            return (
              <line
                key={`l${i}`}
                className="vh-sign-link"
                x1={ax * sign.width} y1={ay * sign.height}
                x2={bx * sign.width} y2={by * sign.height}
                vectorEffect="non-scaling-stroke"
              />
            );
          })}
          {sign.stars.map(([x, y, w], i) => (
            <g
              key={i}
              className="vh-sign-star"
              transform={`translate(${x * sign.width} ${y * sign.height})`}
              style={{ '--vh-twinkle-delay': `${((i * 0.61) % 2.7).toFixed(2)}s` }}
            >
              <circle className="vh-sign-star-halo" fill={`url(#vh-sign-glare-${classKey})`} r={sign.height * (0.008 + 0.012 * w)} />
              {w >= 0.9 ? (
                /* Four tapered diffraction spikes on the brightest stars. */
                <path className="vh-sign-star-rays" d={sparkle(sign.height * 0.016, sign.height * 0.0012)} />
              ) : null}
              <circle className="vh-sign-star-core" r={sign.height * (0.0015 + 0.0025 * w)} />
            </g>
          ))}
        </svg>
      </div>
    );
  }
}
